/**
 * Form 8233 Service
 * Handles all data operations for Form 8233 using model fields as single source of truth
 */

import {
  Form8233Model,
  Form8233ResponseModel,
  Form8233FieldMapper,
} from "../Models/Form8233Models";
import { BASE_URL } from "../App_Settings/app_settings";
import { getUserIdFromLocalStorage } from "../API_Endpoints/APIEndpoints";
import { updateFileData } from "./fileDataService";

export class Form8233Service {
  private static readonly DRAFT_STORAGE_KEY = "form8233_draft";
  private static readonly API_ENDPOINT = `${BASE_URL}/FileData/ImportFiles`;

  /**
   * Create a new Form 8233 model from API response data
   * Maps API response fields to model fields
   */
  static fromApiResponse(apiData: any): Form8233Model {
    const responseModel = new Form8233ResponseModel(apiData);

    // Use the field mapper for proper conversion
    const formModel = Form8233FieldMapper.mapResponseToForm(responseModel);

    // Handle legacy field mappings
    this.mapLegacyApiFields(apiData, formModel);

    return formModel;
  }

  /**
   * Map legacy API field names to current model fields
   */
  private static mapLegacyApiFields(apiData: any, model: Form8233Model): void {
    Object.keys(apiData).forEach((key) => {
      const value = apiData[key];
      if (key.toLowerCase().includes("country") || key.toLowerCase().includes("residence")) {
        // console.log(`  📍 ${key} = "${value}"`);
      }
    });

    const legacyMappings: Record<string, string[]> = {
      // Core form fields
      "Form-8233-Calendar_year": [
        "Form-8233-Calendar_year",
        "Calendar_year",
        "calendar_year",
        "year",
      ],
      "Form-8233-Beginning_year": ["Form-8233-Beginning_year", "Beginning_year", "beginning_year"],
      "Form-8233-Ending_year": ["Form-8233-Ending_year", "Ending_year", "ending_year"],
      Form_name: ["Form_name", "form_name"],
      Form_revision_date: ["Form_revision_date", "form_revision"],
      UniqueIdentifier: ["UniqueIdentifier", "unique_identifier", "id"],

      // Part 1 - Individual Information
      "Form-8233-Part_1_individual_name": [
        "Form-8233-Part_1_individual_name",
        "Individual_name",
        "individual_name",
        "name",
      ],
      "Form-8233-Part_1_us_tax_id": [
        "Form-8233-Part_1_us_tax_id",
        "US_tax_id",
        "us_tax_id",
        "us_tin",
      ],
      "Form-8233-Part_1_ftin": ["Form-8233-Part_1_ftin", "FTIN", "ftin", "foreign_tin"],

      // Part 1 - Address Information (API fields)
      "Form-8233-Part_1_permanent_address_street": ["Form-8233-Part_1_permanent_address_street"],
      "Form-8233-Part_1_permanent_address_city_state_postal": [
        "Form-8233-Part_1_permanent_address_city_state_postal",
        "Permanent_address_city_state_postal",
        "permanent_city",
      ],
      "Form-8233-Part_1_permanent_address_country": ["Form-8233-Part_1_permanent_address_country"],
      "Form-8233-Part_1_us_address_street": ["Form-8233-Part_1_us_address_street"],
      "Form-8233-Part_1_us_address_city_state_zip": ["Form-8233-Part_1_us_address_city_state_zip"],

      // Part 1 - Immigration Information
      "Form-8233-Part_1_us_visa_type": [
        "Form-8233-Part_1_us_visa_type",
        "US_visa_type",
        "visa_type",
      ],
      "Form-8233-Part_1_passport_country": [
        "Form-8233-Part_1_passport_country",
        "Passport_country",
        "passport_country",
      ],
      "Form-8233-Part_1_passport_number": [
        "Form-8233-Part_1_passport_number",
        "Passport_number",
        "passport_number",
      ],
      "Form-8233-Part_1_date_entry_to_us": [
        "Form-8233-Part_1_date_entry_to_us",
        "Date_entry_to_us",
        "entry_date",
      ],
      "Form-8233-Part_1_current_nonimmigrant_status": [
        "Form-8233-Part_1_current_nonimmigrant_status",
        "Current_nonimmigrant_status",
        "nonimmigrant_status",
      ],
      "Form-8233-Part_1_current_nonimmigrant_status_expires": [
        "Form-8233-Part_1_current_nonimmigrant_status_expires",
        "Current_nonimmigrant_status_expires",
        "status_expires",
      ],

      // Part 2 - Personal Services (API fields)
      "Form-8233-Part_2_personal_services": [
        "Form-8233-Part_2_personal_services",
        "Personal_services",
        "personal_services",
      ],
      "Form-8233-Part_2_total_compensation_expected": [
        "Form-8233-Part_2_total_compensation_expected",
        "Total_compensation_expected",
        "compensation_expected",
      ],
      "Form-8233-Part_2_tax_treaty": ["Form-8233-Part_2_tax_treaty", "Tax_treaty", "treaty"],
      "Form-8233-Part_2_total_compensation_listed_tax_under_treaty": [
        "Form-8233-Part_2_total_compensation_listed_tax_under_treaty",
        "Total_compensation_listed_tax_under_treaty",
        "compensation_under_treaty",
      ],
      "Form-8233-Part_2_sufficient_facts": [
        "Form-8233-Part_2_sufficient_facts",
        "Sufficient_facts",
        "sufficient_facts",
      ],

      "12d_Other": [
        "Form-8233-Part_2_country_residence_other",
        "Country_residence_other",
        "residence_other",
      ],
      "12_All is exempt from tax": [
        "Form-8233-Part_2_all_exempt",
        "All_exempt",
        "all_exempt_from_tax",
      ],

      // Part 4 - Withholding Agent Information
      "Form-8233-Part_4_withholding_name": [
        "Form-8233-Part_4_withholding_name",
        "Withholding_name",
        "withholding_name",
      ],
      "Form-8233-Part_4_eid": ["Form-8233-Part_4_eid", "EID", "eid"],
      "Form-8233-Part_4_address_street": [
        "Form-8233-Part_4_address_street",
        "Address_street",
        "address_street",
      ],
      "Form-8233-Part_4_address_city_state_zip": [
        "Form-8233-Part_4_address_city_state_zip",
        "Address_city_state_zip",
        "address_city_state_zip",
      ],
      "Form-8233-Part_4_telephone_number": [
        "Form-8233-Part_4_telephone_number",
        "Telephone_number",
        "telephone_number",
      ],
      "Form-8233-Part_4_signature": [
        "Form-8233-Part_4_signature",
        "Part_4_signature",
        "withholding_signature",
      ],
      "Form-8233-Part_4_signature_date": [
        "Form-8233-Part_4_signature_date",
        "Part_4_signature_date",
        "withholding_signature_date",
      ],

      "Permanent address city": ["Permanent_address_city", "permanent_city"],
      "Permanent address state NonUS": ["Permanent_address_state_NonUS", "permanent_state"],
      "Permanent address zip": ["Permanent_address_zip", "permanent_zip", "permanent_postal"],
      "US address line 1": ["US address line 1"],
      "US address city": ["US address city"],
      "US address state USCA": ["US address state USCA"],
      "US address zip": ["US address zip"],
      "US address country": ["US address country"],

      "Taxpayer type": ["Taxpayer_type", "taxpayer_type"],
    };

    // Handle boolean conversions for checkbox fields
    const checkboxFields = ["certify"];
    checkboxFields.forEach((field) => {
      if (apiData.hasOwnProperty(field)) {
        const value = apiData[field];
        if (typeof value === "string") {
          (model as any)[field] =
            value.toLowerCase() === "true" || value === "1" || value.toLowerCase() === "yes";
        } else {
          (model as any)[field] = Boolean(value);
        }
      }
    });

    const possibleApiFields = legacyMappings;
    Object.entries(possibleApiFields).forEach(([modelField, apiFieldOptions]) => {
      for (const apiField of apiFieldOptions) {
        if (apiData.hasOwnProperty(apiField)) {
          (model as any)[modelField] = apiData[apiField];
          break;
        }
      }
    });

    if (apiData["Form-8233-Part_2_country_residence_other"] && !model["12d_Other"]) {
      model["12d_Other"] = apiData["Form-8233-Part_2_country_residence_other"];
    }

    // Handle special field processing
    this.processSpecialFields(apiData, model);

    // Log any API fields that weren't mapped
    const mappedApiFields = new Set();
    Object.values(possibleApiFields)
      .flat()
      .forEach((field) => mappedApiFields.add(field));

    Object.keys(apiData).forEach((apiField) => {
      if (!mappedApiFields.has(apiField) && apiField !== "formType" && apiField !== "viewPdf") {
      }
    });
  }

  /**
   * Process special fields that need custom logic
   */
  private static processSpecialFields(apiData: any, model: Form8233Model): void {
    // Parse permanent address components
    if (apiData["Form-8233-Part_1_permanent_address_city_state_postal"]) {
      const addressComponents = this.parseAddressComponents(
        apiData["Form-8233-Part_1_permanent_address_city_state_postal"]
      );

      model["Permanent address city"] = addressComponents.city;
      model["Permanent address state NonUS"] = addressComponents.state;
      model["Permanent address zip"] = addressComponents.postalCode;
    }

    // Parse US address components
    if (apiData["Form-8233-Part_1_us_address_city_state_zip"]) {
      const addressComponents = this.parseAddressComponents(
        apiData["Form-8233-Part_1_us_address_city_state_zip"]
      );

      // Set both the actual form field names and model field names
      model["US address city"] = addressComponents.city;
      model["US address state USCA"] = addressComponents.state;
      model["US address zip"] = addressComponents.postalCode;
    }

    // Determine taxpayer type based on US tax ID format
    if (apiData["Form-8233-Part_1_us_tax_id"]) {
      const taxId = apiData["Form-8233-Part_1_us_tax_id"];
      const taxpayerType = this.determineTaxpayerType(taxId);
      model["Taxpayer type"] = taxpayerType;
    }
  }

  /**
   * Parse address string into components with smart state/zip detection
   */
  private static parseAddressComponents(addressString: string): {
    city: string;
    state: string;
    postalCode: string;
  } {
    if (!addressString) return { city: "", state: "", postalCode: "" };

    const result = { city: "", state: "", postalCode: "" };

    // First, try to extract postal code from the entire string using international patterns
    const postalCodePatterns = [
      /\b(\d{5}(?:-\d{4})?)\b/g, // US ZIP codes (12345, 12345-6789)
      /\b(\d{4})\b/g, // Many countries use 4-digit codes
      /\b(\d{6})\b/g, // Some countries use 6-digit codes
      /\b([A-Z]\d[A-Z]\s?\d[A-Z]\d)\b/gi, // Canadian postal codes
    ];

    let extractedPostalCode = "";
    for (const pattern of postalCodePatterns) {
      const match = addressString.match(pattern);
      if (match && match[0]) {
        extractedPostalCode = match[0].trim();
        result.postalCode = extractedPostalCode;
        break;
      }
    }

    // Remove the postal code from the string for further parsing
    let remainingAddress = addressString;
    if (extractedPostalCode) {
      remainingAddress = addressString.replace(extractedPostalCode, "").trim();
    }

    // Handle different address formats
    if (remainingAddress.includes(",")) {
      // Comma-separated format: "City, State" or "City, State, Zip"
      const parts = remainingAddress
        .split(",")
        .map((part) => part.trim())
        .filter((part) => part);
      if (parts.length > 0) result.city = parts[0];
      if (parts.length > 1) result.state = parts[1];
      if (parts.length > 2 && !result.postalCode) result.postalCode = parts[2];
    } else {
      // Space-separated format: "Sofia 1000" or "Madison WI 53706"
      const parts = remainingAddress.split(/\s+/).filter((part) => part);

      if (parts.length === 1) {
        // Only city
        result.city = parts[0];
      } else if (parts.length === 2) {
        // Could be "City Zip" or "City State"
        const lastPart = parts[1];
        result.city = parts[0];

        // Check if last part is a state code (2 letters)
        if (/^[A-Z]{2}$/i.test(lastPart)) {
          result.state = lastPart.toUpperCase();
        } else {
          // Assume it's part of the city if no postal code was extracted
          if (!result.postalCode) {
            result.city = remainingAddress;
          }
        }
      } else if (parts.length >= 3) {
        // "Madison WI 53706" format
        result.city = parts[0];

        // Check if second part is a state code
        if (/^[A-Z]{2}$/i.test(parts[1])) {
          result.state = parts[1].toUpperCase();
          // Remaining parts could be additional postal info
          if (!result.postalCode && parts.length > 2) {
            result.postalCode = parts.slice(2).join(" ");
          }
        } else {
          // Multi-word city name
          const stateIndex = parts.findIndex((part) => /^[A-Z]{2}$/i.test(part));
          if (stateIndex > 0) {
            result.city = parts.slice(0, stateIndex).join(" ");
            result.state = parts[stateIndex].toUpperCase();
            if (!result.postalCode && stateIndex < parts.length - 1) {
              result.postalCode = parts.slice(stateIndex + 1).join(" ");
            }
          } else {
            result.city = remainingAddress;
          }
        }
      }
    }

    // If state contains postal code that wasn't extracted, try to separate it
    if (result.state && !result.postalCode) {
      const stateMatch = result.state.match(/^([A-Z]{2})\s+(.+)$/i);
      if (stateMatch) {
        result.state = stateMatch[1].toUpperCase();
        result.postalCode = stateMatch[2];
      }
    }

    return result;
  }

  /**
   * Determine taxpayer type based on TIN format
   */
  private static determineTaxpayerType(taxId: string): string {
    if (!taxId) return "";

    // Remove any hyphens or spaces for analysis
    const cleanTaxId = taxId.replace(/[-\s]/g, "");

    // EIN format: XX-XXXXXXX (9 digits, starts with 2 digits)
    if (/^\d{9}$/.test(cleanTaxId) && cleanTaxId.length === 9) {
      const firstTwoDigits = parseInt(cleanTaxId.substring(0, 2));
      // EIN prefixes typically range from 01-99, but exclude SSN-like patterns
      if (firstTwoDigits >= 10) {
        return "EIN";
      }
    }

    // SSN/ITIN format: XXX-XX-XXXX (9 digits)
    if (/^\d{9}$/.test(cleanTaxId) && cleanTaxId.length === 9) {
      return "SSN/ITIN";
    }

    // GIIN format: typically longer and alphanumeric
    if (cleanTaxId.length > 9 || /[A-Za-z]/.test(cleanTaxId)) {
      return "GIIN";
    }

    // Default to SSN/ITIN if we can't determine
    return "SSN/ITIN";
  }

  /**
   * Save form data to API
   */
  static async saveToApi(
    model: Form8233Model,
    row: any,
    selectedIndex: number = 0
  ): Promise<boolean> {
    try {
      // Enhanced authentication with multiple fallbacks
      let userData;
      let token;

      try {
        userData = getUserIdFromLocalStorage();
        token = userData?.token;
      } catch (error) {
        console.log("Form8233 Auth - getUserIdFromLocalStorage failed:", error);
      }

      // Fallback authentication methods
      if (!token) {
        try {
          const storedUserData = localStorage.getItem("userData");
          if (storedUserData) {
            const parsed = JSON.parse(storedUserData);
            token = parsed.token;
          }
        } catch (error) {
          console.log("Form8233 Auth - fallback userData parsing failed:", error);
        }
      }

      if (!token) {
        try {
          token = localStorage.getItem("token");
        } catch (error) {
          console.log("Form8233 Auth - direct token retrieval failed:", error);
        }
      }

      if (!token) {
        console.error("Form8233 Auth - No authentication token found after all fallbacks");
        throw new Error("No authentication token found");
      }

      // Convert model to API payload
      const formData = { ...model };

      // Remove unwanted address fields before processing
      formData["Form-8233-Part_1_permanent_address_city_state_postal"] = "";
      formData["Form-8233-Part_1_us_address_city_state_zip"] = "";

      let parsedFormsData;
      try {
        // Parse formsData and check if it's complex structure or simple array
        parsedFormsData = JSON.parse(row.formsData || "[]");

        // Check if it's the complex structure with formsData property and withholdingData
        if (
          parsedFormsData &&
          typeof parsedFormsData === "object" &&
          parsedFormsData.formsData &&
          Array.isArray(parsedFormsData.formsData)
        ) {
          // Find and update the Form8233 form in the complex structure
          let formUpdated = false;

          for (let i = 0; i < parsedFormsData.formsData.length; i++) {
            const formItem = parsedFormsData.formsData[i];
            if (formItem && formItem.data && Array.isArray(formItem.data)) {
              for (let j = 0; j < formItem.data.length; j++) {
                const dataItem = formItem.data[j];
                if (dataItem && (dataItem.formType === "8233" || dataItem.Form_number === "8233")) {
                  parsedFormsData.formsData[i].data[j] = {
                    ...dataItem,
                    ...formData,
                  };
                  formUpdated = true;
                  break;
                }
              }
              if (formUpdated) break;
            }
          }

          if (!formUpdated) {
            parsedFormsData.formsData.push({
              request_id: "new-8233-" + Date.now(),
              status: "success",
              data: [formData],
              reportdata: {
                requestId: "new-8233-" + Date.now(),
                subRequestId: "new-8233-" + Date.now(),
                taxForms: "8233",
                type: "extraction",
              },
            });
          }
        } else {
          // Handle as simple array

          const formsDataArray = Array.isArray(parsedFormsData) ? parsedFormsData : [];

          // Update the form at the specified index (or add if it doesn't exist)

          const currentItem = formsDataArray[selectedIndex];

          if (selectedIndex < formsDataArray.length && currentItem) {
            formsDataArray[selectedIndex] = {
              ...currentItem,
              ...formData,
            };
          } else {
            while (formsDataArray.length <= selectedIndex) {
              formsDataArray.push({});
            }
            formsDataArray[selectedIndex] = formData;
          }

          parsedFormsData = formsDataArray;
        }
      } catch (error) {
        console.log("Form8233: Error parsing existing formsData, creating new array:", error);
        parsedFormsData = [formData];
      }

      // Store back as stringified structure (preserving complex format if applicable)
      row.formsData = JSON.stringify(parsedFormsData);

      // Send to API using centralized service
      await updateFileData(row);

      return true;
    } catch (error) {
      console.error("Failed to save Form 8233:", error);
      throw error;
    }
  }
}
